import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

export const useSessionTimeout = () => {
  const { toast, dismiss } = useToast();
  const [warningToastId, setWarningToastId] = useState<string | null>(null);

  const refreshSession = async () => {
    const { error } = await supabase.auth.refreshSession();
    
    if (error) {
      toast({
        title: "Refresh Failed",
        description: "Could not refresh your session. Please log in again.",
        variant: "destructive",
      });
    } else {
      if (warningToastId) {
        dismiss(warningToastId);
        setWarningToastId(null);
      }
      toast({
        title: "Session Refreshed",
        description: "Your session has been extended successfully.",
      });
    }
  };

  useEffect(() => {
    let warningTimer: ReturnType<typeof setTimeout>;
    let checkInterval: ReturnType<typeof setInterval>;

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.expires_at) return;

      const expiresAt = session.expires_at * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

      // If session expires in less than 5 minutes, show warning immediately
      if (timeUntilExpiry <= fiveMinutes && timeUntilExpiry > 0) {
        showWarning();
      } else if (timeUntilExpiry > fiveMinutes) {
        // Set timer to show warning 5 minutes before expiry
        const timeUntilWarning = timeUntilExpiry - fiveMinutes;
        warningTimer = setTimeout(() => {
          showWarning();
        }, timeUntilWarning);
      }
    };

    const showWarning = () => {
      const { id } = toast({
        title: "Session Expiring Soon",
        description: "Your admin session will expire in 5 minutes. Click below to stay logged in.",
        duration: Infinity, // Keep toast visible until dismissed
        action: (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshSession}
          >
            Refresh Session
          </Button>
        ),
      });
      setWarningToastId(id);
    };

    // Initial check
    checkSession();

    // Check session every minute to handle edge cases
    checkInterval = setInterval(checkSession, 60 * 1000);

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED') {
        // Session was refreshed, dismiss warning and recheck
        if (warningToastId) {
          dismiss(warningToastId);
          setWarningToastId(null);
        }
        checkSession();
      } else if (event === 'SIGNED_OUT') {
        // Clear timers on sign out
        if (warningTimer) clearTimeout(warningTimer);
        if (checkInterval) clearInterval(checkInterval);
        if (warningToastId) {
          dismiss(warningToastId);
          setWarningToastId(null);
        }
      }
    });

    return () => {
      if (warningTimer) clearTimeout(warningTimer);
      if (checkInterval) clearInterval(checkInterval);
      subscription.unsubscribe();
      if (warningToastId) dismiss(warningToastId);
    };
  }, [toast, dismiss, warningToastId]);

  return { refreshSession };
};
